import { useState, useMemo } from 'react';
import { Order } from '@/lib/api';
import { TrendingUp, Package, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

const DELIVERY_FEE = 150;

function toLocalDateStr(isoStr: string): string {
  const d = new Date(isoStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatDateArabic(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('ar-SA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}

function formatTime(isoStr: string): string {
  const d = new Date(isoStr);
  return d.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' });
}

function getTodayStr(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

export default function DriverEarnings({ orders }: { orders: Order[] }) {
  const today = getTodayStr();
  const [selectedDate, setSelectedDate] = useState(today);

  const completedOrders = useMemo(
    () => orders.filter(o => o.status === 'completed' && o.completed_at),
    [orders]
  );

  const todayOrders = useMemo(
    () => completedOrders.filter(o => toLocalDateStr(o.completed_at!) === today),
    [completedOrders, today]
  );

  const selectedOrders = useMemo(
    () => completedOrders.filter(o => toLocalDateStr(o.completed_at!) === selectedDate),
    [completedOrders, selectedDate]
  );

  const todayCount = todayOrders.length;
  const todayEarnings = todayCount * DELIVERY_FEE;
  const isViewingToday = selectedDate === today;

  const shiftDate = (days: number) => {
    const d = new Date(selectedDate + 'T00:00:00');
    d.setDate(d.getDate() + days);
    const newStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (newStr > today) return;
    setSelectedDate(newStr);
  };

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Today Stats - always visible */}
      <div className="bg-gradient-to-l from-cyan-800/30 to-cyan-900/20 border border-cyan-700/30 rounded-2xl p-5">
        <p className="text-navy-300 text-sm mb-3">أرباح اليوم</p>
        <div className="flex items-end justify-between">
          <div>
            <p className="text-3xl font-bold text-white">{todayEarnings.toLocaleString('ar-SA')}</p>
            <p className="text-navy-400 text-sm mt-1">ريال</p>
          </div>
          <div className="text-left">
            <div className="flex items-center gap-1.5 text-cyan-400">
              <Package className="w-4 h-4" />
              <span className="text-lg font-bold">{todayCount}</span>
            </div>
            <p className="text-navy-400 text-xs">توصيلة</p>
          </div>
        </div>
      </div>

      {/* Date selector */}
      <div className="bg-navy-800/50 border border-navy-700 rounded-xl p-3">
        <div className="flex items-center justify-between">
          <button
            onClick={() => shiftDate(-1)}
            className="text-navy-400 hover:text-white p-2 transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 flex-1 justify-center">
            <Calendar className="w-4 h-4 text-cyan-400" />
            <input
              type="date"
              value={selectedDate}
              max={today}
              onChange={e => setSelectedDate(e.target.value || today)}
              className="bg-transparent text-white text-sm font-medium border-none outline-none cursor-pointer"
              dir="ltr"
            />
          </div>
          <button
            onClick={() => shiftDate(1)}
            disabled={selectedDate >= today}
            className="text-navy-400 hover:text-white p-2 transition-colors disabled:opacity-30"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        </div>
        <p className="text-center text-navy-400 text-xs mt-1">
          {isViewingToday ? 'اليوم' : formatDateArabic(selectedDate)}
        </p>
      </div>

      {/* Selected date stats (only show if not today) */}
      {!isViewingToday && (
        <div className="bg-navy-800/50 border border-navy-700 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-cyan-400" />
            <span className="text-navy-300 text-sm">عمولة هذا اليوم</span>
          </div>
          <div className="text-left">
            <span className="text-white font-bold">{(selectedOrders.length * DELIVERY_FEE).toLocaleString('ar-SA')}</span>
            <span className="text-navy-400 text-sm mr-1">ريال</span>
            <span className="text-navy-500 text-xs mr-2">({selectedOrders.length} توصيلة)</span>
          </div>
        </div>
      )}

      {/* Orders list */}
      <div>
        <h3 className="text-navy-300 text-sm font-medium mb-3">
          {isViewingToday ? 'توصيلات اليوم' : `توصيلات ${formatDateArabic(selectedDate)}`}
        </h3>
        {selectedOrders.length === 0 ? (
          <div className="text-center py-10 text-navy-500">
            <Package className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p className="text-sm">لا توجد توصيلات مكتملة في هذا اليوم</p>
          </div>
        ) : (
          <div className="space-y-2">
            {selectedOrders.map(order => (
              <div key={order.id} className="bg-navy-800/40 border border-navy-700/50 rounded-xl px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="text-white text-sm font-medium">{order.customer_name}</p>
                  <p className="text-navy-500 text-xs" dir="ltr">{order.order_number}</p>
                </div>
                <div className="text-left">
                  <p className="text-cyan-400 font-bold text-sm">+{DELIVERY_FEE}</p>
                  <p className="text-navy-500 text-xs">{formatTime(order.completed_at!)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
